import { Shortlist } from "@/lib/types";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import { useLocation } from "wouter";

interface ShortlistCardProps {
  shortlist: Shortlist;
  onClick: () => void;
}
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import BulkEmailModal from "@/components/players/BulkEmailModal";

export default function ShortlistCard({ shortlist, onClick }: ShortlistCardProps) {
  const { toast } = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [, navigate] = useLocation();
  
  // Use data coordinator for unified data access
  const { players } = useDataCoordinator();
  
  // Get players in this shortlist
  const { data: shortlistWithPlayers } = useQuery({
    queryKey: [`/api/shortlists/${shortlist.id}`],
    enabled: !!shortlist.id,
  });
  
  const playerCount = (shortlistWithPlayers as any)?.players?.length || 0;
  const databasePercentage = players.length > 0 
    ? Math.round((playerCount / players.length) * 100) 
    : 0;
  
  // Delete shortlist mutation
  const deleteShortlistMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/shortlists/${shortlist.id}`, {});
      if (!response.ok) {
        let errorMessage = "Failed to delete shortlist";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          errorMessage = response.statusText || `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Shortlist deleted",
        description: `"${shortlist.name}" has been successfully deleted.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      setIsDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      console.error("Delete shortlist error:", error);
      toast({
        title: "Error",
        description: `Failed to delete shortlist: ${error.message || 'Please try again'}`,
        variant: "destructive",
      });
    },
  });
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the settings menu or its content
    if ((e.target as HTMLElement).closest('[data-settings-menu]') || 
        (e.target as HTMLElement).closest('.dropdown-menu-content')) {
      e.stopPropagation();
      return;
    }
    onClick();
  };
  
  // This ensures the delete action doesn't trigger the card click
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleteDialogOpen(true);
  };

  // Handle Email All button click
  const handleEmailAllClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!shortlistWithPlayers || !Array.isArray((shortlistWithPlayers as any)?.players)) {
      toast({
        title: "Unable to load players",
        description: "Please wait for the shortlist to load completely.",
        variant: "destructive",
      });
      return;
    }

    const playersInShortlist = (shortlistWithPlayers as any).players;
    const playersWithEmails = playersInShortlist.filter((player: any) => player.email);
    
    if (playersWithEmails.length === 0) {
      toast({
        title: "No email addresses found",
        description: "No players in this shortlist have email addresses.",
        variant: "destructive",
      });
      return;
    }

    // Open the bulk email modal
    setShowBulkEmailModal(true);
  };
  
  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-neutral-200 hover:shadow-md transition-shadow cursor-pointer relative"
      onClick={handleCardClick}
    >
      {/* Settings Dropdown */}
      <div className="absolute top-3 right-3 z-10" data-settings-menu>
        <DropdownMenu>
          <DropdownMenuTrigger className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-neutral-100">
            <i className="fas fa-ellipsis-v text-neutral-500"></i>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="dropdown-menu-content">
            <DropdownMenuItem 
              className="text-red-600 cursor-pointer"
              onClick={handleDeleteClick}
            >
              <i className="fas fa-trash mr-2"></i> Delete shortlist
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary">
            <i className="fas fa-star"></i>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-neutral-800">{shortlist.name}</h3>
            <p className="text-xs text-neutral-500">Created on {formatDate(shortlist.created_at)}</p>
          </div>
        </div>
        
        <p className="text-sm text-neutral-600 mb-4 h-12 overflow-hidden">
          {shortlist.description || 'No description provided.'}
        </p>
        
        <div className="flex justify-between items-center text-sm">
          <div>
            <span className="font-medium text-neutral-800">{playerCount}</span>
            <span className="text-neutral-500"> players</span>
          </div>
          <div>
            <span className="text-neutral-500">{databasePercentage}% of database</span>
          </div>
        </div>
        
        <div className="mt-2 w-full bg-neutral-200 rounded-full h-1.5">
          <div 
            className="bg-primary h-1.5 rounded-full" 
            style={{ width: `${databasePercentage}%` }}
          ></div>
        </div>
      </div>
      
      <div className="border-t border-neutral-200 py-3 px-6 flex justify-between">
        <button 
          className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center space-x-1 transition-colors"
          onClick={handleEmailAllClick}
          disabled={playerCount === 0}
          title={playerCount === 0 ? "No players in shortlist" : `Email all ${playerCount} players`}
        >
          <i className="fas fa-envelope mr-1"></i> 
          <span>Email All</span>
          {playerCount > 0 && (
            <span className="text-xs bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full ml-1">
              {playerCount}
            </span>
          )}
        </button>
        <button className="text-sm text-neutral-500 hover:text-neutral-700">
          <i className="fas fa-arrow-right"></i> View
        </button>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shortlist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the shortlist "{shortlist.name}"? 
              This action cannot be undone, and all player associations will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteShortlistMutation.mutate()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteShortlistMutation.isPending ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Deleting...
                </>
              ) : (
                <>Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => setShowBulkEmailModal(false)}
        players={(shortlistWithPlayers as any)?.players || []}
        title={`Email ${shortlist.name} Players`}
        subtitle={`Send email to all players in this shortlist`}
      />
    </div>
  );
}